import { QuantitySelectorComponent } from '@theme/component-quantity-selector';

/**
 * A custom element that allows the user to select a quantity in the cart.
 * Extends QuantitySelectorComponent but uses absolute max limits instead of effective max.
 * Semantics: "What should the total quantity BE in the cart" vs "How many to ADD to cart"
 *
 * @extends {QuantitySelectorComponent}
 */
export class CartQuantitySelectorComponent extends QuantitySelectorComponent {
  /** Delai avant que la confirmation de retrait ne se desarme d'elle-meme. */
  static REMOVAL_TIMEOUT = 5000;

  /** @type {boolean} Le prochain clic sur « - » retire-t-il la ligne ? */
  removalArmed = false;

  /** @type {ReturnType<typeof setTimeout> | null} */
  removalTimer = null;

  /** @type {HTMLParagraphElement | null} */
  removalMessage = null;

  /**
   * Gets the effective maximum value for cart quantity selector
   * Cart page: uses absolute max (how much can be in cart total)
   * @returns {number | null} The effective max, or null if no max
   */
  getEffectiveMax() {
    const { max } = this.getCurrentValues();
    return max; // Cart uses absolute max, not max minus cart quantity
  }

  disconnectedCallback() {
    this.disarmRemoval();
    super.disconnectedCallback();
  }

  /**
   * Updates button states based on current value and limits
   * Cart buttons are always managed client-side, never server-disabled
   *
   * Difference avec le parent : le « - » reste actif quand la quantite vaut 1.
   * Un bouton grise a 1 est un cul-de-sac — la cliente doit alors chercher la
   * corbeille. Ici le « - » descend jusqu'a 0, ce qui retire la ligne.
   */
  updateButtonStates() {
    const { minusButton, plusButton } = this.refs;
    const { value } = this.getCurrentValues();
    const effectiveMax = this.getEffectiveMax();

    // Cart buttons are always dynamically managed
    minusButton.disabled = value <= 0;
    plusButton.disabled = effectiveMax !== null && value >= effectiveMax;
  }

  /**
   * Updates quantity by a given step.
   *
   * Dans le panier, descendre sous le minimum veut dire « je n'en veux plus ».
   * Mais un retrait est irreversible cote client : la gravure saisie disparait
   * avec la ligne. Le premier clic annonce donc l'intention, le second seul
   * l'execute. Passe le delai, on oublie — un panier ne doit pas rester arme.
   *
   * @param {number} stepMultiplier - Positive for increase, negative for decrease
   */
  updateQuantity(stepMultiplier) {
    const { min, step, value } = this.getCurrentValues();

    if (stepMultiplier < 0 && value - step < min) {
      if (!this.removalArmed) {
        this.armRemoval();
        return;
      }

      const { quantityInput } = this.refs;

      this.disarmRemoval();
      quantityInput.value = '0';
      this.onQuantityChange();
      this.updateButtonStates();
      return;
    }

    // Toute autre action vaut renoncement : on ne laisse pas un « + » distrait
    // rearmer un retrait au clic suivant.
    this.disarmRemoval();
    super.updateQuantity(stepMultiplier);
  }

  /**
   * Premier clic : affiche la demande de confirmation sous la ligne.
   */
  armRemoval() {
    this.removalArmed = true;

    if (!this.removalMessage) {
      const message = document.createElement('p');

      message.className = 'cart-removal-confirm';
      message.setAttribute('role', 'status');
      message.setAttribute('aria-live', 'polite');
      message.textContent = 'Encore un appui sur « − » pour retirer cet article.';
      message.style.cssText = [
        'margin:6px 0 0',
        'font-size:0.75rem',
        'line-height:1.45',
        'opacity:0.72',
      ].join(';');

      this.removalMessage = message;
    }

    this.after(this.removalMessage);
    this.refs.minusButton.setAttribute('aria-description', 'Appuyez une seconde fois pour retirer cet article du panier.');

    this.removalTimer = setTimeout(
      () => this.disarmRemoval(),
      CartQuantitySelectorComponent.REMOVAL_TIMEOUT
    );
  }

  /**
   * Retour a l'etat neutre : plus de message, plus de retrait au clic suivant.
   */
  disarmRemoval() {
    this.removalArmed = false;

    if (this.removalTimer) {
      clearTimeout(this.removalTimer);
      this.removalTimer = null;
    }

    this.removalMessage?.remove();
    this.refs.minusButton?.removeAttribute('aria-description');
  }
}

if (!customElements.get('cart-quantity-selector-component')) {
  customElements.define('cart-quantity-selector-component', CartQuantitySelectorComponent);
}
